/// FOURJS_START_COPYRIGHT(D,2024)
/// Property of Four Js*
/// (c) Copyright Four Js 2024, 2026. All Rights Reserved.
/// * Trademark of Four Js Development Tools Europe Ltd
///   in the United States and elsewhere
///
/// This file can be modified by licensees according to the
/// product manual.
/// FOURJS_END_COPYRIGHT

"use strict";

modulum("StartMenuService", ["InitService", "MainContainerService"], function (context, cls) {
  /**
   * StartMenu service
   * @namespace gbc.StartMenuService
   * @gbcService
   */
  class StartMenuService /** @lends gbc.StartMenuService */ {
    constructor() {
      /**
       * @type {string}
       */
      this._categoryName = "other";

      /**
       * Active shortcuts currently visible at the bottom
       * @type {HTMLElement[]}
       */
      this._visibleShortcuts = [];
    }

    /**
     * Init service
     */
    init() {
      context.MainContainerService.when(context.constants.widgetEvents.sideBarDrawerShown, this.refreshStartMenuShortcuts.bind(this));
    }

    /**
     * Add a startmenu element in sidebar drawer
     * @param {classes.StartMenuWidget} widget
     */
    add(widget) {
      if (widget) {
        context.MainContainerService.addSideBarDrawerItems(widget.getText() ? widget.getText() : this._categoryName, widget.getElement());
        context.MainContainerService.showOpenDrawerItem();
        context.MainContainerService.toggleAppsCategoryItem(true);

        this._trackStartMenuVisibility(widget);

        if (context.MainContainerService.shouldAutoDockSideBarDrawer() && !context.MainContainerService.isSideBarDrawerEmpty()) {
          context.MainContainerService.showSideBarDrawer();
          context.MainContainerService.dockSideBarDrawer();
        }
      }
    }

    /**
     * Remove a startmenu element from sidebar drawer
     * @param {classes.StartMenuWidget} widget
     */
    remove(widget) {
      if (!widget) {
        return;
      }
      const element = widget.getElement();

      if (element._visibilityShortcut) {
        this._removeVisibleShortcut(element._visibilityShortcut);
        element._visibilityShortcut.remove();
        delete element._visibilityShortcut;
      }

      if (element._visibilityObserver) {
        element._visibilityObserver.disconnect();
        delete element._visibilityObserver;
      }

      context.MainContainerService.removeSideBarDrawerItem(widget.getText() ? widget.getText() : this._categoryName, widget.getElement());
      if (!context.MainContainerService.getSideBarDrawerActiveCategories().has("other")) {
        context.MainContainerService.toggleAppsCategoryItem(false);
      }
      // Hide and undock the SideBarDrawer if is empty
      if (context.MainContainerService.isSideBarDrawerEmpty()) {
        context.MainContainerService.hideSideBarDrawer();
        context.NavigationButtonService.hideAppsCategory();
        context.MainContainerService.hideOpenDrawerItem();
      }
    }

    /**
     * Remove a shortcut from the visible list and update its position
     * @param {HTMLElement} shortcut
     */
    _removeVisibleShortcut(shortcut) {
      const i = this._visibleShortcuts.indexOf(shortcut);
      if (i !== -1) {
        this._visibleShortcuts.splice(i, 1);
        this._updateShortcutPositions();
      }
    }

    /**
     * Observe a StartMenuWidget and show shortcut if it's out of view
     * @param {classes.StartMenuWidget} widget
     */
    _trackStartMenuVisibility(widget) {
      const container = context.MainContainerService.getSideBarDrawerContainer();
      const element = widget.getElement();
      const title = widget.getTextElement();

      if (!title) {
        return;
      }

      // Create shortcut button
      const shortcut = document.createElement("div");
      shortcut.classList.add("gbc_StartMenuWidget__shortcut", "hidden");

      shortcut.addEventListener("click", () => {
        if (!widget.hasClass("gbc_open")) {
          widget.toggleMenu();
        }
        element.scrollIntoView({ behavior: "smooth", block: "start" });
      });

      element._visibilityShortcut = shortcut;
      context.MainContainerService.getSideBarDrawer().appendChild(shortcut);

      // Observer callback
      const onVisibilityChange = ([entry]) => {
        if (!entry.target || !shortcut) {
          return;
        }
        if (shortcut.textContent !== widget.getText()) {
          shortcut.textContent = widget.getText();
        }
        this._updateShortcutVisibility(entry.target, shortcut);
      };

      // Observe the title inside the widget
      const observer = new IntersectionObserver(onVisibilityChange, {
        root: container,
        threshold: 1.0
      });

      observer.observe(title);
      element._visibilityObserver = observer;
    }

    /**
     * Check if a given widget's shortcut should be visible or not
     * @param {HTMLElement} title
     * @param {HTMLElement} shortcut
     */
    _updateShortcutVisibility(title, shortcut) {
      const container = context.MainContainerService.getSideBarDrawerContainer();
      const containerRect = container.getBoundingClientRect();
      const titleRect = title.getBoundingClientRect();
      const outOfView = titleRect.bottom > containerRect.bottom;
      const isVisible = !shortcut.classList.contains("hidden");

      if (outOfView && !isVisible) {
        shortcut.classList.remove("hidden");
        this._visibleShortcuts.push(shortcut);
        this._updateShortcutPositions();
      } else if (!outOfView && isVisible) {
        shortcut.classList.add("hidden");
        this._removeVisibleShortcut(shortcut);
      }
    }

    /**
     * Recalculate shortcut positions at the bottom
     */
    _updateShortcutPositions() {
      this._visibleShortcuts.forEach((element, index) => {
        element.style.bottom = `${index * element.offsetHeight}px`;
      });
    }

    /**
     * Refresh visibility state of all start menu shortcuts
     */
    refreshStartMenuShortcuts() {
      const container = context.MainContainerService.getSideBarDrawerContainer();
      const widgets = container.querySelectorAll(".gbc_StartMenuWidget");

      widgets.forEach((el) => {
        const title = el.querySelector(".gbc_StartMenuWidget__title");
        const shortcut = el._visibilityShortcut;
        if (title && shortcut) {
          this._updateShortcutVisibility(title, shortcut);
        }
      });
    }
  }
  gbc.StartMenuService = new StartMenuService();
  context.InitService.register(context.StartMenuService);
});
